import { useLocation, useNavigate } from 'react-router-dom';
import { IBillingManagement } from '../../../types/billing-management.type';
import { useManagementBilling } from '../components/customHook/useManagementBilling';
import { getStatusLabels } from '../components/TableBillingManagement';
import Loading from '@/common/components/ui/loading/Loading';
import { useState } from 'react';
import ButtonWithLoader from '@/common/components/ui/loading/ButtonLoading';
import { useToastContext } from '@/common/components/ui/toast/toast.context';
import { adjustSubscription } from '@/common/services/payments.api';
import InfoItem from '@/common/components/ui/form/infoItem';
import { formatDate, formatDateAndTime } from '@/common/helpers/date.helpers';

export default function ManagementBillingDash() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const { notifySuccess, notifyError } = useToastContext();
  const payment: IBillingManagement | undefined = state?.payment;

  const {
    details,
    subscriptionId,
    percentualValue,
    isLoading,
    error,
    paymentSplitWalletId
  } = useManagementBilling(payment);

  const paymentDetails = details?.payment;
  const renter = details?.renter;
  const expectedPartner = details?.expectedPartner;
  const splitPartner = details?.splitPartner;
  const historyVehicles = renter?.vehicleHistory || [];

  if (!payment) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold text-red-600">Nenhum dado de pagamento encontrado</h1>
        <p>Volte para a página anterior e selecione uma fatura.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold text-red-600">Erro ao carregar dados</h1>
        <p>{error}</p>
      </div>
    );
  }


  const handleAdjustedSubscription = async () => {
    const paymentData = {
      paymentId: payment.paymentId,
      subscriptionId: subscriptionId,
      correctWalletId: expectedPartner?.walletId,
      percentualValue: percentualValue,
      partnerCompany: payment.partnerCompany,

    };
    try {
      setIsLoadingButton(true);
      // Aqui você deve chamar a função que ajusta a assinatura
      const response = await adjustSubscription(paymentData.paymentId, paymentData);

      if (response.ok) {
        notifySuccess("Assinatura ajustada com sucesso!");
      } else {
        notifyError("Erro ao ajustar assinatura: " + response.message as string);
      }
    } catch (error) {
      console.error("Erro ao ajustar assinatura:", error);
      notifyError("Erro ao ajustar assinatura.");
    } finally {
      setIsLoadingButton(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 mt-4 bg-white rounded-lg shadow-lg border border-gray-100">
      <div className="border-b pb-2 mb-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl md:text-3xl font-bold text-black">
            Detalhes da Fatura
          </h1>
          <span className={`px-4 py-2 rounded-full text-base font-semibold ${payment.splitDone === false
            ? 'bg-red-100 text-red-800 animate-pulse'
            : 'bg-blue-100 text-blue-800'
            }`}>
            {getStatusLabels(payment.status)}
          </span>
        </div>
        <div className="mt-1">
          <span>
            <a
              href={`https://asaas.com/payment/show/${payment.invoiceNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`hover:underline transition-colors ${payment.splitDone === false
                ? 'text-red-600 hover:text-red-800'
                : 'text-blue-800 hover:text-blue-600'
                }`}
            >
              #{payment.invoiceNumber}
            </a>
          </span>
          <div>
            <h2 className="text-lg  mb-1 border-b "></h2>
            <div className="space-y-3 mb-1 border-b ">
              <InfoItem label="Descrição:" value={paymentDetails?.description} />
              <InfoItem label="Valor do percentual:" value={percentualValue} />
              <InfoItem label="Valor do split:" value={details?.split?.totalValue ? `R$ ${details.split.totalValue.toFixed(2)}` : "-"} />
              <InfoItem label="Motivo da divergência:" value={details?.reason || payment.reason} />
              <InfoItem label="Origem da placa considerada:" value={details?.plateSource || payment.plateSource} />
              <InfoItem label="Criada em:" value={payment.createdAt ? formatDateAndTime(payment.createdAt) : "-"} />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 p-2 rounded-lg border bg-gray-50 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Detalhes da Fatura</h2>
          <div className="space-y-3">
            <InfoItem label="Proprietário do veículo:" value={payment.partnerCompany} />
            <InfoItem label="Carteira do proprietário:" value={expectedPartner?.walletId} />
            <InfoItem label="Placa esperada da cobrança:" value={payment.expectedLicensePlate || payment.licensePlate} />
            <InfoItem label="Placa encontrada na descrição:" value={payment.descriptionLicensePlate} />
            <InfoItem label="Placa do vehicleId da cobrança:" value={payment.paymentVehicleLicensePlate} />
            <InfoItem label="Placa do veículo esperado:" value={details?.expectedVehicle?.licensePlate} />
            <InfoItem label="Carteira de recebimento do split:" value={payment.splitWalletId} />
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border bg-gray-50 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Informações de Distribuição Financeira</h2>
          <div className="space-y-3">
            <InfoItem label="Parceiro esperado da cobrança:" value={payment.partnerCompany} />
            <InfoItem label="Parceiro que recebeu o split:" value={payment.splitPartnerCompany || splitPartner?.companyName} />
            <InfoItem label="Carteira de Destino:" value={paymentSplitWalletId} />
            <InfoItem label="Status do split:" value={payment.splitStatus} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 p-4 rounded-lg border shadow-sm">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Renter</h2>
          <div className="space-y-3">
            <InfoItem label="Nome:" value={renter?.name} />
            <InfoItem label="CPF:" value={renter?.cpf} />
            <InfoItem label="Status:" value={renter?.status} />
            <InfoItem label="WhatsApp:" value={renter?.whatsapp} />
            <InfoItem label="Customer ID Asaas:" value={renter?.paymentCustomerId} />
            <InfoItem label="Moto atual do renter:" value={renter?.currentVehicle?.licensePlate} />
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border shadow-sm">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Referências da Cobrança</h2>
          <div className="space-y-3">
            <InfoItem label="VehicleId salvo na cobrança:" value={payment.paymentVehicleId} />
            <InfoItem label="Vehicle esperado:" value={details?.expectedVehicle?._id} />
            <InfoItem label="Vehicle da descrição:" value={details?.descriptionVehicle?._id} />
            <InfoItem label="Vehicle do payment:" value={details?.paymentVehicle?._id} />
            <InfoItem label="Partner esperado:" value={details?.expectedPartner?.companyName} />
            <InfoItem label="Partner do split:" value={details?.splitPartner?.companyName} />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg border shadow-sm mb-4">
        <h2 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Histórico de Motos do Renter</h2>
        {historyVehicles.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum histórico de moto encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Placa</th>
                  <th className="py-2 pr-4">Início</th>
                  <th className="py-2 pr-4">Fim</th>
                  <th className="py-2 pr-4">Partner</th>
                </tr>
              </thead>
              <tbody>
                {[...historyVehicles].reverse().map((vehicleHistory, index) => (
                  <tr key={`${vehicleHistory.id || vehicleHistory.licensePlate || "history"}-${index}`} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{vehicleHistory.licensePlate || "-"}</td>
                    <td className="py-2 pr-4">{vehicleHistory.vehicleHistoryInitial ? formatDate(vehicleHistory.vehicleHistoryInitial) : "-"}</td>
                    <td className="py-2 pr-4">{vehicleHistory.vehicleHistoryEnd ? formatDate(vehicleHistory.vehicleHistoryEnd) : "-"}</td>
                    <td className="py-2 pr-4">{vehicleHistory.partnerId || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ações (se necessário) */}
      <div className="flex justify-between space-x-3 mt-4 border-t pt-2">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray5 rounded-lg hover:bg-gray transition">
          Voltar
        </button>
        {(payment.splitDone === false) && (
          <ButtonWithLoader
            type='button'
            isLoading={isLoadingButton}
            onClick={handleAdjustedSubscription}
          >
            Corrigir
          </ButtonWithLoader>
          // <button className="px-4 py-2 bg-redPrimary text-white rounded hover:bg-red-300 transition">
          //   Corrigir
          // </button>
        )}
      </div>
    </div>
  );
}
